import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrencySymbol } from '../../constants/currencies';
import { useLanguage } from '../../contexts/LanguageContext';

interface RobustCurrencyInputProps {
  value: string | number;
  onValueChange: (value: string, formattedValue?: string) => void;
  currency: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  allowNegativeValue?: boolean;
  'data-testid'?: string;
}

export const RobustCurrencyInput: React.FC<RobustCurrencyInputProps> = ({
  value,
  onValueChange,
  currency,
  className = '',
  placeholder,
  disabled = false,
  id,
  name,
  allowNegativeValue = false,
  'data-testid': dataTestId,
}) => {
  const { language } = useLanguage();
  const inputRef = useRef<HTMLInputElement>(null);
  const currencySymbol = getCurrencySymbol(currency);
  
  // Determine separators based on language
  const isGerman = language.startsWith('de');
  const decimalSeparator = isGerman ? ',' : '.';
  const thousandSeparator = isGerman ? '.' : ',';
  
  // Convert backend value (always "123456.78" format) to display format
  const formatForDisplay = useCallback((val: string | number): string => {
    if (val === null || val === undefined || val === '') return '';
    
    // Convert to string and handle the backend format
    let numStr = typeof val === 'number' ? val.toString() : val;
    
    // Remove any existing formatting (for re-formatting)
    numStr = numStr.replace(/[^\d.-]/g, '');
    
    // Handle negative numbers
    const isNegative = numStr.startsWith('-');
    if (isNegative) numStr = numStr.substring(1);
    
    // Parse as float to ensure proper number
    const num = parseFloat(numStr);
    if (isNaN(num)) return '';
    
    // Split into integer and decimal parts
    const parts = num.toFixed(2).split('.');
    let integerPart = parts[0];
    const decimalPart = parts[1];
    
    // Add thousand separators
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator);
    
    // Combine with proper decimal separator
    let formatted = integerPart + decimalSeparator + decimalPart;
    
    // Add negative sign if needed
    if (isNegative && allowNegativeValue) {
      formatted = '-' + formatted;
    }
    
    return formatted;
  }, [isGerman, decimalSeparator, thousandSeparator, allowNegativeValue]);
  
  // Convert display format to backend format (always "123456.78")
  const parseToBackend = useCallback((displayValue: string): string => {
    if (!displayValue) return '0.00';
    
    // Handle negative numbers
    const isNegative = displayValue.startsWith('-');
    let cleanValue = isNegative ? displayValue.substring(1) : displayValue;
    
    // Remove thousand separators
    cleanValue = cleanValue.replace(new RegExp('\\' + thousandSeparator, 'g'), '');
    
    // Replace decimal separator with dot for backend
    cleanValue = cleanValue.replace(decimalSeparator, '.');
    
    // Remove any non-numeric characters except dot
    cleanValue = cleanValue.replace(/[^\d.]/g, '');
    
    // Parse and validate
    const num = parseFloat(cleanValue);
    if (isNaN(num)) return '0.00';
    
    // Format to 2 decimal places
    let result = num.toFixed(2);
    
    // Add negative sign if needed
    if (isNegative && allowNegativeValue) {
      result = '-' + result;
    }
    
    return result;
  }, [decimalSeparator, thousandSeparator, allowNegativeValue]);
  
  // State for the displayed value
  const [displayValue, setDisplayValue] = useState(() => formatForDisplay(value));
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  
  // Update display when value prop changes
  useEffect(() => {
    setDisplayValue(formatForDisplay(value));
  }, [value, formatForDisplay]);
  
  // Restore cursor position after formatting
  useEffect(() => {
    if (cursorPosition !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [displayValue, cursorPosition]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    const currentCursorPos = input.selectionStart || 0;
    
    // Allow empty input
    if (rawValue === '') {
      setDisplayValue('');
      onValueChange('0.00', '');
      return;
    }
    
    // Check if user is typing a negative sign at the beginning
    if (allowNegativeValue && rawValue === '-') {
      setDisplayValue('-');
      return;
    }
    
    // Count separators before and after for cursor adjustment
    const sepCountBefore = (displayValue.match(new RegExp('\\' + thousandSeparator, 'g')) || []).length;
    
    // Parse and format the new value
    const backendValue = parseToBackend(rawValue);
    const newDisplayValue = formatForDisplay(backendValue);
    
    // Count separators in new value
    const sepCountAfter = (newDisplayValue.match(new RegExp('\\' + thousandSeparator, 'g')) || []).length;
    
    // Calculate cursor position adjustment
    const sepDiff = sepCountAfter - sepCountBefore;
    let newCursorPos = currentCursorPos;
    
    // Adjust cursor position based on separator changes
    if (sepDiff !== 0) {
      // Check if we're before any thousand separator
      const beforeCursor = rawValue.substring(0, currentCursorPos);
      const sepInBefore = (beforeCursor.match(new RegExp('\\' + thousandSeparator, 'g')) || []).length;
      const newBeforeCursor = newDisplayValue.substring(0, currentCursorPos + sepDiff);
      const sepInNewBefore = (newBeforeCursor.match(new RegExp('\\' + thousandSeparator, 'g')) || []).length;
      
      if (sepInNewBefore > sepInBefore) {
        newCursorPos = currentCursorPos + (sepInNewBefore - sepInBefore);
      }
    }
    
    setDisplayValue(newDisplayValue);
    setCursorPosition(newCursorPos);
    onValueChange(backendValue, newDisplayValue);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    const cursorPos = input.selectionStart || 0;
    const value = input.value;
    
    // Allow navigation keys
    if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab'].includes(e.key)) {
      return;
    }
    
    // Allow selection-related keys
    if (e.ctrlKey || e.metaKey) {
      return;
    }
    
    // Handle decimal separator input
    if (e.key === '.' || e.key === ',') {
      e.preventDefault();
      
      // Check if decimal separator already exists
      if (value.includes(decimalSeparator)) {
        // Move cursor to decimal position
        const decimalPos = value.indexOf(decimalSeparator);
        input.setSelectionRange(decimalPos + 1, decimalPos + 1);
      } else {
        // Add decimal separator
        const beforeCursor = value.substring(0, cursorPos);
        const afterCursor = value.substring(cursorPos);
        const newValue = beforeCursor + decimalSeparator + afterCursor;
        
        setDisplayValue(newValue);
        setCursorPosition(cursorPos + 1);
        
        // Process the change
        const backendValue = parseToBackend(newValue);
        onValueChange(backendValue, formatForDisplay(backendValue));
      }
      return;
    }
    
    // Block non-numeric keys (except allowed ones)
    if (!/[\d\-]/.test(e.key) && !['Backspace', 'Delete'].includes(e.key)) {
      e.preventDefault();
    }
    
    // Handle minus sign
    if (e.key === '-' && (!allowNegativeValue || cursorPos !== 0 || value.includes('-'))) {
      e.preventDefault();
    }
  };
  
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Select all on focus for easy replacement
    if (e.target.value === formatForDisplay(0)) {
      e.target.select();
    }
  };
  
  const handleBlur = () => {
    // Ensure proper formatting on blur
    if (displayValue === '' || displayValue === '-') {
      const formatted = formatForDisplay(0);
      setDisplayValue(formatted);
      onValueChange('0.00', formatted);
    } else {
      const backendValue = parseToBackend(displayValue);
      const formatted = formatForDisplay(backendValue);
      setDisplayValue(formatted);
      onValueChange(backendValue, formatted);
    }
  };
  
  // Set default placeholder based on language
  const defaultPlaceholder = isGerman ? '0,00' : '0.00';
  
  // Extract width classes from className for the wrapper div
  const widthClasses = className.match(/\b(w-\S+|flex-\S+|min-w-\S+|max-w-\S+)\b/g) || [];
  const wrapperClasses = widthClasses.length > 0 ? widthClasses.join(' ') : '';
  
  return (
    <div className={`relative ${wrapperClasses}`}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder || defaultPlaceholder}
        className={`${className} pr-12`}
        disabled={disabled}
        id={id}
        name={name}
        data-testid={dataTestId}
      />
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <span className="text-text-tertiary text-sm font-mono">
          {currencySymbol}
        </span>
      </div>
    </div>
  );
};

export default RobustCurrencyInput;